// =============================================
// COMPONENTE: FLOW STATS PANEL
// =============================================
// Arquivo: FlowStatsPanel.tsx
// Descrição: Painel de estatísticas e validação do fluxo
// =============================================

import React, { useMemo } from 'react';
import { Node, Edge } from 'reactflow';
import { X, AlertTriangle, AlertCircle, Info, CheckCircle, Activity } from 'lucide-react';
import { validateFlow, getFlowHealthScore } from './FlowValidator';
import './FlowStatsPanel.css';

// =============================================
// INTERFACES
// =============================================

interface FlowStatsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  nodes: Node[];
  edges: Edge[];
  onNodeClick?: (nodeId: string) => void;
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

const FlowStatsPanel: React.FC<FlowStatsPanelProps> = ({
  isOpen,
  onClose,
  nodes,
  edges,
  onNodeClick
}) => {
  // =============================================
  // VALIDAÇÃO E MÉTRICAS
  // =============================================

  const issues = useMemo(() => validateFlow(nodes, edges), [nodes, edges]);
  const healthScore = useMemo(() => getFlowHealthScore(issues), [issues]);

  const stats = useMemo(() => {
    const nodeTypes = nodes.reduce((acc, node) => {
      const type = node.type || 'unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const avgConnectionsPerNode = nodes.length > 0 ? edges.length / nodes.length : 0;

    const startNodes = nodes.filter(n => n.type === 'start' || n.type === 'webhook_trigger' || n.type === 'scheduler');
    const endNodes = nodes.filter(n => n.type === 'end');

    return {
      totalNodes: nodes.length,
      totalEdges: edges.length,
      nodeTypes,
      avgConnectionsPerNode: avgConnectionsPerNode.toFixed(1),
      startPoints: startNodes.length,
      endPoints: endNodes.length,
      errorCount: issues.filter(i => i.type === 'error').length,
      warningCount: issues.filter(i => i.type === 'warning').length,
      infoCount: issues.filter(i => i.type === 'info').length
    };
  }, [nodes, edges, issues]);

  // =============================================
  // CATEGORIAS DE TIPOS DE NÓS
  // =============================================

  const nodeTypeLabels: Record<string, string> = {
    start: 'Início',
    end: 'Fim',
    message: 'Mensagem',
    input: 'Input',
    condition: 'Condição',
    action: 'Ação',
    webhook: 'Webhook',
    delay: 'Delay',
    database: 'Database',
    http_request: 'HTTP Request',
    javascript_function: 'JavaScript',
    webhook_trigger: 'Webhook Trigger',
    internal_api: 'API Interna',
    whatsapp_advanced: 'WhatsApp',
    loop: 'Loop',
    error_handler: 'Try/Catch',
    email: 'Email',
    scheduler: 'Agendamento'
  };

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  if (!isOpen) return null;

  return (
    <div className="flow-stats-overlay" onClick={onClose}>
      <div className="flow-stats-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flow-stats-header">
          <div className="flow-stats-title">
            <Activity size={20} />
            <h2>Estatísticas & Validação</h2>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flow-stats-content">
          {/* Health Score */}
          <div className="flow-health-card">
            <div className="health-score-circle" style={{ borderColor: healthScore.color }}>
              <div className="health-score-value" style={{ color: healthScore.color }}>
                {healthScore.score}
              </div>
              <div className="health-score-label">Saúde do Fluxo</div>
            </div>
            <div className="health-status">
              <div className="health-status-badge" style={{ background: healthScore.color }}>
                {healthScore.status === 'excellent' && 'Excelente'}
                {healthScore.status === 'good' && 'Bom'}
                {healthScore.status === 'fair' && 'Regular'}
                {healthScore.status === 'poor' && 'Precisa melhorar'}
              </div>
              <p className="health-description">
                {stats.errorCount === 0 && stats.warningCount === 0 && 'Seu fluxo está perfeito!'}
                {stats.errorCount > 0 && `${stats.errorCount} erro(s) encontrado(s)`}
                {stats.errorCount === 0 && stats.warningCount > 0 && `${stats.warningCount} aviso(s) encontrado(s)`}
              </p>
            </div>
          </div>

          {/* Estatísticas Gerais */}
          <div className="stats-section">
            <h3>Estatísticas Gerais</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <i className="fas fa-circle-nodes"></i>
                <div className="stat-content">
                  <span className="stat-value">{stats.totalNodes}</span>
                  <span className="stat-label">Total de Nós</span>
                </div>
              </div>
              <div className="stat-item">
                <i className="fas fa-diagram-project"></i>
                <div className="stat-content">
                  <span className="stat-value">{stats.totalEdges}</span>
                  <span className="stat-label">Conexões</span>
                </div>
              </div>
              <div className="stat-item">
                <i className="fas fa-play-circle"></i>
                <div className="stat-content">
                  <span className="stat-value">{stats.startPoints}</span>
                  <span className="stat-label">Pontos de Início</span>
                </div>
              </div>
              <div className="stat-item">
                <i className="fas fa-flag-checkered"></i>
                <div className="stat-content">
                  <span className="stat-value">{stats.endPoints}</span>
                  <span className="stat-label">Pontos de Fim</span>
                </div>
              </div>
              <div className="stat-item">
                <i className="fas fa-link"></i>
                <div className="stat-content">
                  <span className="stat-value">{stats.avgConnectionsPerNode}</span>
                  <span className="stat-label">Conexões/Nó</span>
                </div>
              </div>
            </div>
          </div>

          {/* Distribuição de Tipos de Nós */}
          <div className="stats-section">
            <h3>Distribuição de Nós</h3>
            <div className="node-types-list">
              {Object.entries(stats.nodeTypes)
                .sort(([, a], [, b]) => b - a)
                .map(([type, count]) => (
                  <div key={type} className="node-type-item">
                    <span className="node-type-label">
                      {nodeTypeLabels[type] || type}
                    </span>
                    <div className="node-type-bar-container">
                      <div
                        className="node-type-bar"
                        style={{
                          width: `${(count / stats.totalNodes) * 100}%`
                        }}
                      />
                    </div>
                    <span className="node-type-count">{count}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Issues de Validação */}
          <div className="stats-section">
            <h3>Problemas Encontrados</h3>
            {issues.length === 0 ? (
              <div className="no-issues">
                <CheckCircle size={48} color="#10b981" />
                <p>Nenhum problema encontrado!</p>
                <span>Seu fluxo está validado e pronto para uso.</span>
              </div>
            ) : (
              <div className="issues-list">
                {issues.map((issue) => (
                  <div
                    key={issue.id}
                    className={`issue-item issue-${issue.type}`}
                    onClick={() => issue.nodeId && onNodeClick && onNodeClick(issue.nodeId)}
                    style={{ cursor: issue.nodeId ? 'pointer' : 'default' }}
                  >
                    <div className="issue-icon">
                      {issue.type === 'error' && <AlertCircle size={20} />}
                      {issue.type === 'warning' && <AlertTriangle size={20} />}
                      {issue.type === 'info' && <Info size={20} />}
                    </div>
                    <div className="issue-content">
                      <div className="issue-header">
                        <span className="issue-title">{issue.title}</span>
                        <span className={`issue-severity severity-${issue.severity}`}>
                          {issue.severity === 'high' && 'Alto'}
                          {issue.severity === 'medium' && 'Médio'}
                          {issue.severity === 'low' && 'Baixo'}
                        </span>
                      </div>
                      <p className="issue-message">{issue.message}</p>
                      {issue.suggestion && (
                        <p className="issue-suggestion">
                          <i className="fas fa-lightbulb"></i>
                          {issue.suggestion}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowStatsPanel;
